import {
  bandProgress,
  clamp01,
  railOffset,
  resolveScrollSurface,
  sceneProgress,
} from '@theme/loumi-scroll-core';

const desktop = window.matchMedia('(min-width: 900px)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const SCENE_SELECTOR = '[data-loumi-scene]';
const REVEAL_SELECTOR = '[data-loumi-in]';

let scrollSurface = window;
let revealObserver;
let frame = 0;
let pointerFrame = 0;
let pointerTarget = { x: 0, y: 0 };
let pointerCurrent = { x: 0, y: 0 };
const heroControllers = new WeakMap();

function getScrollSurface() {
  const candidate = document.querySelector('.page-wrapper');
  if (!candidate) return window;
  const mode = resolveScrollSurface({
    overflowY: getComputedStyle(candidate).overflowY,
    scrollHeight: candidate.scrollHeight,
    clientHeight: candidate.clientHeight,
  });
  return mode === 'container' ? candidate : window;
}

function headerOffset() {
  const header = document.querySelector('header-component, .header-section');
  return header ? Math.min(header.getBoundingClientRect().height, 116) : 0;
}

function progressFor(scene) {
  const rect = scene.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const kind = scene.dataset.loumiScene;

  if (kind === 'hero') {
    return clamp01((headerOffset() - rect.top) / Math.max(rect.height * 0.82, 1));
  }

  if (kind === 'map') {
    const offset = headerOffset();
    return clamp01((offset - rect.top) / Math.max(rect.height - viewportHeight + offset, 1));
  }

  return sceneProgress({ top: rect.top, height: rect.height, viewportHeight });
}

function updateGuide(scene, progress) {
  const path = scene.querySelector('[data-loumi-guide-path]');
  const marker = scene.querySelector('[data-loumi-guide-marker]');
  if (!path || !marker || typeof path.getTotalLength !== 'function') return;

  const length = path.getTotalLength();
  const point = path.getPointAtLength(length * clamp01(progress));
  marker.setAttribute('transform', `translate(${point.x} ${point.y})`);
}

function updateScene(scene) {
  const progress = progressFor(scene);
  scene.style.setProperty('--loumi-scene-p', progress.toFixed(4));

  for (const layer of scene.querySelectorAll('[data-loumi-depth]')) {
    const depth = Number.parseFloat(layer.dataset.loumiDepth || '0');
    const shift = (progress - 0.35) * depth * -140;
    layer.style.setProperty('--loumi-depth-shift', `${shift.toFixed(2)}px`);
  }

  if (scene.dataset.loumiScene === 'map') updateGuide(scene, progress);

  const bands = [...scene.querySelectorAll('[data-loumi-band]')].filter((band) => !band.closest('[hidden]'));
  bands.forEach((band, index) => {
    band.style.setProperty('--loumi-band-p', bandProgress(progress, index, bands.length).toFixed(4));
  });

  if (scene.dataset.loumiScene === 'shelf' && desktop.matches) {
    const stage = scene.querySelector('.loumi-moment-shelf__viewport');
    const rail = scene.querySelector('[data-loumi-shelf-rail]');
    if (stage && rail) {
      const offset = railOffset(progress, rail.scrollWidth, stage.clientWidth);
      rail.style.setProperty('--loumi-rail-x', `${offset.toFixed(2)}px`);
    }
  }
}

function updateScenes() {
  frame = 0;
  document.querySelectorAll(SCENE_SELECTOR).forEach(updateScene);
}

function requestSceneUpdate() {
  if (frame) return;
  frame = window.requestAnimationFrame(updateScenes);
}

function createRevealObserver() {
  revealObserver?.disconnect();
  revealObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    }
  }, {
    root: scrollSurface === window ? null : scrollSurface,
    rootMargin: '0px 0px -12% 0px',
    threshold: 0.08,
  });

  document.querySelectorAll(REVEAL_SELECTOR).forEach((element) => revealObserver.observe(element));
}

function bindScrollSurface() {
  const nextSurface = getScrollSurface();
  if (nextSurface !== scrollSurface) {
    scrollSurface.removeEventListener('scroll', requestSceneUpdate);
    scrollSurface = nextSurface;
  }
  scrollSurface.removeEventListener('scroll', requestSceneUpdate);
  scrollSurface.addEventListener('scroll', requestSceneUpdate, { passive: true });
  createRevealObserver();
  requestSceneUpdate();
}

function updatePointer() {
  pointerFrame = 0;
  pointerCurrent.x += (pointerTarget.x - pointerCurrent.x) * 0.12;
  pointerCurrent.y += (pointerTarget.y - pointerCurrent.y) * 0.12;
  const hero = document.querySelector('[data-loumi-scene="hero"]');
  if (hero) {
    hero.style.setProperty('--loumi-pointer-x', pointerCurrent.x.toFixed(4));
    hero.style.setProperty('--loumi-pointer-y', pointerCurrent.y.toFixed(4));
  }
  if (Math.abs(pointerTarget.x - pointerCurrent.x) > 0.002 || Math.abs(pointerTarget.y - pointerCurrent.y) > 0.002) {
    pointerFrame = window.requestAnimationFrame(updatePointer);
  }
}

function bindPointer() {
  const hero = document.querySelector('[data-loumi-scene="hero"]');
  if (!hero || !finePointer.matches || hero.dataset.loumiPointerReady === 'true') return;
  hero.dataset.loumiPointerReady = 'true';
  hero.addEventListener('pointermove', (event) => {
    const rect = hero.getBoundingClientRect();
    pointerTarget.x = clamp01((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointerTarget.y = clamp01((event.clientY - rect.top) / rect.height) * 2 - 1;
    if (!pointerFrame) pointerFrame = window.requestAnimationFrame(updatePointer);
  });
  hero.addEventListener('pointerleave', () => {
    pointerTarget = { x: 0, y: 0 };
    if (!pointerFrame) pointerFrame = window.requestAnimationFrame(updatePointer);
  });
}

function setMotionMode() {
  // Owner-requested brand policy. Old stored choices no longer affect the site.
  document.documentElement.classList.add('loumi-scroll-ready');
  document.documentElement.dataset.loumiMotion = 'full';
}

function initializeHero() {
  const hero = document.querySelector('.loumi-stage-hero--float');
  if (!hero || heroControllers.has(hero)) return;
  const abort = new AbortController();
  let intersects = false;
  const setVisible = () => { hero.dataset.heroVisible = String(intersects && !document.hidden); };
  const observer = new IntersectionObserver((entries) => {
    intersects = entries[0].isIntersecting;
    setVisible();
  }, {threshold: 0});
  observer.observe(hero);
  document.addEventListener('visibilitychange', setVisible, {signal: abort.signal});
  heroControllers.set(hero, {abort, observer});
  // The complete static scene stays visible until its real photo atlas is ready.
  Promise.allSettled([...hero.querySelectorAll('.loumi-stage__float img')].map((image) => image.decode?.()))
    .then(() => { if (hero.isConnected) hero.dataset.heroReady = 'true'; });
}

function initialize() {
  setMotionMode();
  bindScrollSurface();
  bindPointer();
  initializeHero();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}
// Shopify layouts can hand scrolling between the document and page wrapper.
// Capture the real scroll event so a late layout change cannot freeze scenes.
document.addEventListener('scroll', requestSceneUpdate, { passive: true, capture: true });
window.addEventListener('pageshow', initialize);
window.addEventListener('resize', bindScrollSurface, { passive: true });
desktop.addEventListener('change', bindScrollSurface);
document.addEventListener('shopify:section:load', initialize);
document.addEventListener('shopify:section:unload', (event) => {
  const hero = event.target.querySelector('.loumi-stage-hero--float');
  const controller = hero && heroControllers.get(hero);
  controller?.abort.abort();
  controller?.observer.disconnect();
  requestSceneUpdate();
});
document.addEventListener('loumi:moment-change', requestSceneUpdate);
